import { create } from 'zustand';
import { VALID_VARIANT_STRINGS } from '@draughts/shared';
import { api } from '../services/api';
import { log } from '../utils/logger';

/**
 * La variante su cui si apre «Gioca».
 *
 * Decisione del 10 ottobre: la variante dell'ULTIMA PARTITA GIOCATA
 * dall'account, letta dal server. Prima «Gioca» si apriva sull'ultima variante
 * toccata in quel browser (`draughts-dashboard-variant`), anche solo per
 * guardare un punteggio nella home: due persone su due varianti vedevano
 * proposte opposte, e lo stesso account cambiava da un dispositivo all'altro.
 *
 * Due fatti:
 *  - `ultima`: l'ultima partita giocata, dal server, con la sua ora;
 *  - `scelta`: una variante scelta DENTRO «Gioca» (per account, in questo
 *    browser). Resta finche' non si gioca una partita dopo.
 *
 * «Dopo» senza confrontare orologi: la scelta ricorda QUALE ultima partita
 * c'era quando e' stata fatta (`base`, l'ora del server). Finche' l'ultima
 * partita e' quella, vale la scelta; quando ne arriva una nuova, vale la nuova.
 * Confrontare l'ora del telefono con quella del server sbagliava di tanto
 * quanto i due orologi non vanno d'accordo.
 */

interface UltimaPartita {
  variant: string;
  /** L'ora della partita, dal server (ms). */
  at: number;
}

interface Scelta {
  variant: string;
  /**
   * L'`at` dell'ultima partita nota quando si e' scelto; `null` se nessuna;
   * `'ignota'` se si e' scelto prima che il server rispondesse (rete lenta,
   * offline): alla prima lettura riuscita la scelta adotta la base letta.
   */
  base: number | null | 'ignota';
}

interface VarianteDiGiocaState {
  /** Di chi sono i dati qui sotto: cambiando account non si eredita niente. */
  userId: string | null;
  /** `true` dopo la prima lettura riuscita per `userId`, anche se non ha mai giocato. */
  caricata: boolean;
  ultima: UltimaPartita | null;
  /** Rilegge l'ultima partita dal server. Senza account non fa niente. */
  aggiorna: (userId: string | null | undefined) => Promise<void>;
  /** La variante scelta dentro «Gioca». */
  scegli: (userId: string | null | undefined, variant: string) => void;
  /** La variante da proporre, o `null` se non c'e' ancora niente da dire. */
  proposta: (userId: string | null | undefined) => string | null;
}

const PREFISSO_CHIAVE = 'draughts-gioca-scelta:';

function variantValida(v: unknown): v is string {
  return typeof v === 'string' && (VALID_VARIANT_STRINGS as readonly string[]).includes(v);
}

/** Le varianti si confrontano in minuscolo: il server puo' mandare l'enum (`ITALIAN`). */
function minuscola(v: string | null | undefined): string | null {
  return typeof v === 'string' ? v.toLowerCase() : null;
}

function leggiScelta(userId: string): Scelta | null {
  try {
    const grezzo = localStorage.getItem(PREFISSO_CHIAVE + userId);
    if (!grezzo) return null;
    const d = JSON.parse(grezzo) as Partial<Scelta>;
    const base = typeof d.base === 'number' || d.base === 'ignota' ? d.base : null;
    return variantValida(d.variant) ? { variant: d.variant, base } : null;
  } catch {
    return null;
  }
}

function scriviScelta(userId: string, scelta: Scelta): void {
  try {
    localStorage.setItem(PREFISSO_CHIAVE + userId, JSON.stringify(scelta));
  } catch {
    /* Navigazione privata o memoria piena: la scelta vale finche' resta aperta la pagina. */
  }
}

export const useVarianteDiGioca = create<VarianteDiGiocaState>((set, get) => ({
  userId: null,
  caricata: false,
  ultima: null,

  aggiorna: async (userId) => {
    if (!userId) return;
    if (get().userId !== userId) set({ userId, caricata: false, ultima: null });
    try {
      const r = await api.getUltimaVariante();
      // Nel frattempo l'account puo' essere cambiato: la risposta non e' piu' sua.
      if (get().userId !== userId) return;
      const at = r.at ? Date.parse(r.at) : NaN;
      const variant = minuscola(r.variant);
      const ultima = variantValida(variant) && !Number.isNaN(at) ? { variant, at } : null;
      set({ caricata: true, ultima });
      /* Una scelta fatta prima di questa risposta prende come base l'ultima
         partita appena letta: resta valida finche' non se ne gioca un'altra. */
      const scelta = leggiScelta(userId);
      if (scelta?.base === 'ignota') scriviScelta(userId, { variant: scelta.variant, base: ultima?.at ?? null });
    } catch (err) {
      log.warn('VarianteDiGioca', 'ultima variante non letta: resta quella che c\'era', {
        errore: err instanceof Error ? err.message : String(err),
      });
    }
  },

  scegli: (userId, scelta) => {
    const variant = minuscola(scelta);
    if (!userId || !variantValida(variant)) return;
    const stato = get();
    /* La base e' l'ultima partita nota ADESSO; se il server non ha ancora
       risposto, «ignota», e la fissera' la prima lettura riuscita. */
    const base = stato.userId === userId && stato.caricata ? stato.ultima?.at ?? null : 'ignota';
    scriviScelta(userId, { variant, base });
  },

  proposta: (userId) => {
    if (!userId) return null;
    const stato = get();
    const scelta = leggiScelta(userId);
    const letta = stato.userId === userId && stato.caricata;
    const ultima = stato.userId === userId ? stato.ultima : null;
    if (scelta) {
      /* Non ancora letta dal server (pagina appena aperta): la scelta, che e'
         l'informazione piu' fresca che c'e' in questo browser. */
      if (!letta || scelta.base === 'ignota') return scelta.variant;
      if (scelta.base === (ultima?.at ?? null)) return scelta.variant;
    }
    return ultima?.variant ?? null;
  },
}));
